import {useArticleApi} from "../../core/hooks/use-article-api";
import React, {useCallback, useEffect, useState} from "react";
import {State, ViewStates} from "@common/components/view-states";
import {ViewState} from "@common/models/enums/viev-state.enum";
import ArticleList from "../components/article-list";
import AppButton from "@common/components/app-button";
import {ReactComponent as PlusIcon} from "@assets/icons/plus.svg";
import ArticleSearchbar from "../components/article-searchbar";
import ArticleFilter from "../components/article-filter";
import EmptyArticleList from "../components/empty-article-list";
import {ArticleSearchResultDto} from "../../core/models/interfaces/article-search-result-dto.interface";
import {useLoading} from "../../core/hooks/use-loading.hook";
import Popover from "@common/components/popover";
import NewArticleActions from "../components/new-article-actions";
import ArticleSorter from "../components/article-sorter";
import {ArticleListRequestParams} from "../../core/models/interfaces/article-list-request-params.interface";
import {mapArticleDetailsToSearchResult} from "../helpers/map-article-details-to-search-result";
import {useActionListener} from "../../core/hooks/use-action.listener";
import {ArticleListResponse} from "../../core/models/interfaces/article-list-response.interface";

const ArticlesListView = () => {
    const {error, getSearchArticles, getUniversalArticle, getPostoperativeUniversalArticle} = useArticleApi();
    const {isLoading} = useLoading();
    const [articles, setArticles] = useState<ArticleSearchResultDto[]>([]);
    const [universalArticle, setUniversalArticle] = useState<ArticleSearchResultDto | null>(null);
    const [postoperativeUniversalArticle, setPostoperativeUniversalArticle] = useState<ArticleSearchResultDto | null>(null);
    const [searchParams, setSearchParams] = useState<ArticleListRequestParams>({
        title: '',
        visibility: 'all',
        sortBy: 'title',
        sortOrder: 'asc'
    });

    const loadArticles = useCallback(async () => {
        try {
            const hasActiveSearch = Boolean(searchParams.title?.trim())
                || searchParams.visibility !== 'all'
                || searchParams.sortBy !== 'title'
                || searchParams.sortOrder !== 'asc';

            if (hasActiveSearch) {
                const result = await getSearchArticles(searchParams);
                setArticles(result);
                setUniversalArticle(null);
                setPostoperativeUniversalArticle(null);
            } else {
                const [result, universal, postoperative] = await Promise.all([
                    getSearchArticles(searchParams),
                    getUniversalArticle(),
                    getPostoperativeUniversalArticle(),
                ]);
                setArticles(result);
                universal && setUniversalArticle(mapArticleDetailsToSearchResult(universal!));
                postoperative && setPostoperativeUniversalArticle(mapArticleDetailsToSearchResult(postoperative!));
            }
        } catch (error) {
            console.error('Błąd podczas ładowania artykułów:', error);
        }
    }, [searchParams]);

    const onSearchResults = (articles: ArticleListResponse): void => {
        setArticles(articles);
        setUniversalArticle(null);
        setPostoperativeUniversalArticle(null);
    }

    const shouldKeepArticle = () => {
        return (a: ArticleSearchResultDto) =>
            (!a.isUniversal || a.id !== universalArticle?.id) &&
            (!a.isPostoperative || a.id !== postoperativeUniversalArticle?.id);
    }

    useActionListener(ArticlesListView, loadArticles);

    useEffect(() => {
        void loadArticles();
    }, [searchParams]);

    return (
        <>
            <div className="flex flex-col gap-6 ">
                <h2 className="font-semibold text-2xl">Artykuły</h2>
                <span className="font-semibold text-lg">Utwórz i edytuj arytykuły, które będą dostępne dla pacjentów w aplikacji.</span>
            </div>
            <div className="bg-white z-10 sticky top-0 flex flex-col gap-4 py-6 border-b border-outline-mild">
                <div className="flex gap-4 items-center">
                    <ArticleSearchbar onResults={onSearchResults} searchParams={searchParams}/>
                    <Popover content={<NewArticleActions/>} offset={4}>
                        <AppButton
                            className="bg-primary w-fit rounded-[4rem] h-[40px] min-w-fit">
                            <div className="flex gap-2 text-red-400 justify-center items-center whitespace-nowrap">
                                <span className="font-semibold text-base text-on-primary ">Nowy artykuł</span>
                                <PlusIcon className="text-on-primary"/>
                            </div>
                        </AppButton>
                    </Popover>
                </div>
                <div className="space-x-2">
                    <div className="inline-block w-fit">
                        <ArticleFilter searchParams={searchParams} onSelect={(value) => setSearchParams(value)}/>
                    </div>
                    <div className="inline-block w-fit">
                        <ArticleSorter searchParams={searchParams} onSelect={(value) => setSearchParams(value)}/>
                    </div>
                </div>
            </div>
            <div className="pt-6">
                <ViewStates loading={isLoading(["getSearchArticles"])} error={error}
                            data={[...articles, universalArticle, postoperativeUniversalArticle]}>
                    <State state={ViewState.LOADING}/>
                    <State state={ViewState.EMPTY}>
                        <EmptyArticleList/>
                    </State>
                    <State state={ViewState.FETCHED}>
                        {(universalArticle || postoperativeUniversalArticle) && (
                            <div className="mb-4">
                                <ArticleList articles={[universalArticle, postoperativeUniversalArticle]}/>
                            </div>
                        )}
                        <ArticleList articles={articles.filter(shouldKeepArticle())}/>
                    </State>
                    <State state={ViewState.ERROR}/>
                </ViewStates>
            </div>
        </>
    );
}

export default ArticlesListView
